import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { KNOWLEDGE_BASES_QUERY_KEYS } from '@/entities/knowledge-base';
import { usePermissions } from '@/entities/user';
import {
  FileRow,
  SortableFileRowKeys,
  useDownloadKnowledgeBaseFiles,
  useKbDocumentSelection,
  useKnowledgeBaseDetail,
  useKnowledgeBaseDetailFiles,
  useKnowledgeBaseFileActions,
} from '@/features/knowledge-bases';
import { notificationService } from '@/lib/services/notification.service';
import { getFileDownloadUrl } from '@/shared/api/assets.api';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks';
import { downloadFile } from '@/shared/utils/downloadFile';
import { useTranslation } from 'react-i18next';
import type { UseCollectionFilesPaneReturn } from './useCollectionFilesPane.types';

const PAGE_SIZE = 10;

/**
 * Files, search, sort, pagination, selection and dialog state for the active
 * collection. Lift-and-shift of the former `KnowledgeBaseDetailInternal`
 * state, minus its two effects: the search-driven page reset now happens in
 * `onSearchChange` itself (no debounce lag), and the redirect-on-error effect
 * is dropped — an unavailable collection is a render decision for
 * `CollectionFilesPane`, not a navigation side effect.
 */
export function useCollectionFilesPane(): UseCollectionFilesPaneReturn {
  const { t } = useTranslation('knowledge');
  const queryClient = useQueryClient();

  const [fileUploaderOpen, setFileUploaderOpen] = useState(false);
  const [fileDetailOpen, setFileDetailOpen] = useState(false);
  const [fileDeleterOpen, setFileDeleterOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<FileRow | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<SortableFileRowKeys>('created');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const debouncedSearchQuery = useDebounce(searchQuery, SEARCH_DEBOUNCE_MS);

  const { knowledgeBase, knowledgeBaseId, isError } = useKnowledgeBaseDetail();

  const {
    canManageAnyKnowledgeBase,
    canManageFoundationalKnowledgeBases,
    canManageWorkspaceKnowledgeBases,
    canManageOrganizationKnowledgeBases,
  } = usePermissions();

  const canModify = useMemo(() => {
    if (!knowledgeBase) return false;
    if (!canManageAnyKnowledgeBase) return false;
    if (knowledgeBase.isInternal) return canManageFoundationalKnowledgeBases;
    if (knowledgeBase.workspaceId) return canManageWorkspaceKnowledgeBases;
    return canManageOrganizationKnowledgeBases;
  }, [
    knowledgeBase,
    canManageAnyKnowledgeBase,
    canManageFoundationalKnowledgeBases,
    canManageWorkspaceKnowledgeBases,
    canManageOrganizationKnowledgeBases,
  ]);

  const {
    files,
    estimatedTotalCount,
    isLoading: isLoadingFiles,
  } = useKnowledgeBaseDetailFiles({
    skip: (page - 1) * PAGE_SIZE,
    take: PAGE_SIZE,
    search: debouncedSearchQuery,
    sortBy,
    sortOrder,
    enabled: !!knowledgeBase && !isError,
  });

  const hasActiveSearch = debouncedSearchQuery.trim().length > 0;
  const totalCount = hasActiveSearch ? estimatedTotalCount : (knowledgeBase?.fileCount ?? 0);

  const invalidateKnowledgeBase = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.all });
    if (knowledgeBaseId) {
      void queryClient.invalidateQueries({
        queryKey: KNOWLEDGE_BASES_QUERY_KEYS.detail(knowledgeBaseId),
      });
    }
  }, [queryClient, knowledgeBaseId]);

  const { uploadFiles, deleteFile, isDeleting, allFiles } = useKnowledgeBaseFileActions({
    knowledgeBaseId,
    updatedFiles: files,
    onUploadSuccess: () => {
      setFileUploaderOpen(false);
      invalidateKnowledgeBase();
    },
    onDeleteSuccess: () => {
      setFileDeleterOpen(false);
      setSelectedFile(null);
      invalidateKnowledgeBase();
    },
  });

  const selection = useKbDocumentSelection({
    pageRows: allFiles,
    totalCount,
    resetKey: debouncedSearchQuery,
  });
  const downloadMutation = useDownloadKnowledgeBaseFiles();

  const onSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const onSort = useCallback((newSortBy: SortableFileRowKeys, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1);
  }, []);

  const onDownloadRow = useCallback(
    (row: FileRow) => {
      if (!row.assetId) return;
      void (async () => {
        try {
          const url = await getFileDownloadUrl(row.assetId!);
          await downloadFile({ url, mimeType: row.mimeType, fileName: row.fileName });
        } catch {
          notificationService.showError(t('knowledgeBaseDetail.failedToDownloadFile'));
        }
      })();
    },
    [t],
  );

  const onBulkDownload = useCallback(() => {
    if (!knowledgeBase || !knowledgeBaseId) return;
    downloadMutation.mutate(
      {
        knowledgeBaseId,
        knowledgeBaseName: knowledgeBase.name ?? 'knowledge-base',
        selectedIds: selection.selectedIds,
        isAllAcrossPages: selection.isAllAcrossPages,
        excludedIds: selection.excludedIds,
        loadedRows: allFiles,
        sortBy,
        sortOrder,
        search: debouncedSearchQuery,
      },
      {
        onSuccess: (count) => {
          notificationService.showSuccess(
            count === 1
              ? t('knowledgeBaseDetail.downloadStarted')
              : t('knowledgeBaseDetail.downloadingDocuments', { documentCount: count }),
          );
          selection.clearSelection();
        },
        onError: (error) => {
          const message =
            error instanceof Error
              ? error.message
              : t('knowledgeBaseDetail.failedToDownloadTheSelected');
          notificationService.showError(message);
        },
      },
    );
  }, [
    knowledgeBase,
    knowledgeBaseId,
    downloadMutation,
    selection,
    allFiles,
    sortBy,
    sortOrder,
    debouncedSearchQuery,
    t,
  ]);

  return {
    files: allFiles,
    totalCount,
    isLoadingFiles,
    page,
    searchQuery,
    hasActiveSearch,
    sortBy,
    sortOrder,
    selection,
    canModify,
    isDownloading: downloadMutation.isPending,
    dialogs: {
      fileUploaderOpen,
      fileDetailOpen,
      fileDeleterOpen,
      isDeleting,
      selectedFile,
    },
    handlers: {
      onSearchChange,
      onPageChange: setPage,
      onSort,
      onViewDetail: (row) => {
        setSelectedFile(row);
        setFileDetailOpen(true);
      },
      onDownloadRow,
      onDeleteRow: (row) => {
        setSelectedFile(row);
        setFileDeleterOpen(true);
      },
      onOpenFileUploader: () => setFileUploaderOpen(true),
      onCloseFileUploader: () => setFileUploaderOpen(false),
      onCloseFileDetail: () => setFileDetailOpen(false),
      onCloseFileDeleter: () => setFileDeleterOpen(false),
      onUpload: uploadFiles,
      onConfirmDelete: () => {
        if (selectedFile) void deleteFile(selectedFile.id);
      },
      onBulkDownload,
    },
  };
}
